import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import ReportStaticInfo from './ReportStaticInfo';
import { useHistory, useParams } from 'react-router-dom';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import Button from '@ssg/common/Components/Button';
import Box from '../../../Components/Layout/Box';
import Header from '@ssg/common/Components/Header';
import Loading from '@ssg/common/Components/Loading';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { faCheck, faEdit, faExclamationTriangle, faFilePdf, faSync, faTimes } from '@fortawesome/pro-regular-svg-icons';

import {
	DeviationColor,
	DeviationInput,
	EsdhFileFragmentFragment,
	EvaluationCategoriesInput,
	EvaluationEnum,
	EvaluationInput,
	SignatureInputType,
	useCreateEndevaluationReportMutation,
	useGetCaseAsbestosReportsAndDocumentsQuery,
	useGetWebCaseQuery,
} from '@ssg/common/GraphQL/indexV2';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import Radio from '@ssg/common/Components/Radio';
import { EvaluationQuestions } from './configurations/Endevalutaion';
import TextAreaInput from './Inputs/TextAreaInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormFieldHeader from '@ssg/common/Components/FormFieldHeader';
import TextInput from './Inputs/TextInput';
import Datepicker from '@ssg/common/Components/Datepicker';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import { tryGetFile } from './checkFilesExist';
import Modal from '@ssg/common/Components/Modal';
import { useUploadSignature } from './hooks/useUploadSignature';
import SignatureInput from './Inputs/SignatureInput';

const EvaluationOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yes', value: EvaluationEnum.Yes },
	{ label: 'reports.asbestos.no', value: EvaluationEnum.No },
	{ label: 'reports.asbestos.notRelevant', value: EvaluationEnum.NotRelevant },
];

const DeviationColorOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yellow', value: DeviationColor.Yellow },
	{ label: 'reports.asbestos.red', value: DeviationColor.Red },
];

interface EvaluationError {
	categoryIndex: number;
	questionIndex: number;
}
interface DeviationError {
	reportIndex: number;
	key: keyof DeviationInput;
}

const throwEvaluationError = (categoryIndex: number, questionIndex, evaluationErrors: EvaluationError[]): string | undefined => {
	const isError = evaluationErrors.filter(re => re.categoryIndex === categoryIndex && re.questionIndex === questionIndex).length > 0;
	if (isError) return 'error.required';
	return;
};
const throwRemarkError = (categoryIndex: number, questionIndex, remarkErrors: EvaluationError[]): string | undefined => {
	const isError = remarkErrors.filter(re => re.categoryIndex === categoryIndex && re.questionIndex === questionIndex).length > 0;
	if (isError) return 'error.required';
	return;
};

const throwDeviationError = (reportIndex: number, key: keyof DeviationInput, deviationErrors: DeviationError[]): string | undefined => {
	const isError = deviationErrors.filter(re => re.reportIndex === reportIndex && re.key === key).length > 0;
	if (isError) return 'error.required';
	return;
};

const populateEmptyLocalstorage = (): EvaluationCategoriesInput[] => {
	const initialData: EvaluationCategoriesInput[] = [];

	EvaluationQuestions.forEach((category, i) => {
		const evaluations: EvaluationInput[] = category.questions.map(question => ({
			index: question.index,
			question: question.question,
			answer: EvaluationEnum.NotSelected,
			remark: '',
		}));

		initialData.push({
			index: i,
			title: category.category,
			evaluations,
		});
	});

	return initialData;
};

interface Params {
	id: string;
}

const EMPTY_DEVIATION_FORM = (relatedEvaluationCategory: string, relatedEvaluationQuestion: string): DeviationInput => ({
	reportDate: formatDateForInput(new Date()),
	relatedEvaluationCategory,
	relatedEvaluationQuestion,
	action: '',
	actionDate: null,
	actionResponsible: '',
	cause: '',
	description: '',
	severityLevel: DeviationColor.NonSelected,
});

const EndEvaluationReport: React.FC = () => {
	const { id: caseId } = useParams<Params>();

	const { t } = useTranslation();
	const history = useHistory();
	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);

	const { loading: caseReportsAndDocumentsLoading } = useGetCaseAsbestosReportsAndDocumentsQuery({
		variables: {
			id: caseId,
		},
		skip: !caseId || caseId === '',
		fetchPolicy: 'cache-and-network',
		onCompleted(data) {
			const formData = removeTypenameGeneric(data.case.caseAsbestosReports?.endevaluationReportFormData);
			if (formData) {
				if (formData.evaluationCategories) {
					const evaluations = removeTypenameGeneric(formData.evaluationCategories);
					setEvaluationCategories(evaluations);
				}
				if (formData.deviations) {
					const sanitizedDeviations = removeTypenameGeneric(formData.deviations);
					setDeviations(sanitizedDeviations);
				}

				if (formData.signature) {
					setSignature(formData.signature);
					if (!formData.signature.signatureDetails?.fileName) {
						return;
					}
					const fileName = formData.signature.signatureDetails.fileName;
					const file = tryGetFile(fileName, data.case.documents);
					setSignatureFile(file);
				}
			}
		},
	});
	const { data: caseData } = useGetWebCaseQuery({ variables: { id: caseId } });
	const erpNumber = caseData?.case.erpNo || '';
	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${caseId}-endevaluation-${name}`, initialValue);

	// const [reportErrors, setReportErrors] = React.useState<ReportError[]>([]);
	const [activeTab, setActiveTab] = React.useState<number>(0);
	const [evaluationCategories, setEvaluationCategories] = useLocalStorage<EvaluationCategoriesInput[]>('evaluationCategories', populateEmptyLocalstorage());

	const [deviations, setDeviations] = useLocalStorage<DeviationInput[]>('deviationReport', []);

	const [signatureFile, setSignatureFile] = React.useState<EsdhFileFragmentFragment | null>();
	const [signature, setSignature] = React.useState<SignatureInputType>();
	const [showSignatureModal, setShowSignatureModal] = React.useState<boolean>(false);
	const [fileError, setFileError] = React.useState<boolean>(false);
	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');

	const [evaluationErrors, setEvaluationError] = React.useState<EvaluationError[]>([]);
	const [remarkErrors, setRemarkErrors] = React.useState<EvaluationError[]>([]);
	const [deviationErrors, setDeviationError] = React.useState<DeviationError[]>([]);

	const [createEndEvaluationReport, { loading: submitting }] = useCreateEndevaluationReportMutation();

	const handleEvaluationChange = (
		categoryIndex: number,
		questionIndex: number,
		key: keyof Pick<EvaluationInput, 'answer' | 'remark'>,
		value: EvaluationInput[keyof Pick<EvaluationInput, 'answer' | 'remark'>],
	) => {
		setEvaluationCategories(currentEvaluations => {
			return currentEvaluations.map(cat =>
				cat.index === categoryIndex
					? {
							...cat,
							evaluations: cat.evaluations?.map(evaluation => (evaluation?.index === questionIndex ? { ...evaluation, [key]: value } : evaluation)),
					  }
					: cat,
			);
		});
		const category = evaluationCategories.find(evaluation => evaluation.index === categoryIndex);
		const question = category?.evaluations?.find(e => e?.index === questionIndex)?.question;
		if (question === undefined || category?.title === undefined) {
			return;
		}
		if (key === 'answer' && value !== EvaluationEnum.NotRelevant) {
			removeRemark(categoryIndex, questionIndex);
		}
		if (key === 'answer' && value === EvaluationEnum.No) {
			addDeviationForm(category?.title, question);
		}
		if (key === 'answer' && value !== EvaluationEnum.No) {
			removeDeviationForm(question);
		}
	};

	const handleDeviationChange = (index: number, key: keyof DeviationInput, value: DeviationInput[keyof DeviationInput]) => {
		setDeviations(currentDeviations =>
			currentDeviations.map((deviation, i) =>
				i === index
					? {
							...deviation,
							[key]: value,
					  }
					: deviation,
			),
		);
	};
	function addDeviationForm(categoryTitle: string, question: string) {
		setDeviations(current => [...current, EMPTY_DEVIATION_FORM(categoryTitle, question)]);
		setActiveTab(deviations.length);
	}

	function removeDeviationForm(question: string) {
		setDeviations(current => current.filter(deviation => deviation.relatedEvaluationQuestion !== question));
		setActiveTab(0);
	}

	const removeRemark = (categoryIndex: number, questionIndex: number) => {
		setEvaluationCategories(currentEvaluations => {
			return currentEvaluations.map(cat =>
				cat.index === categoryIndex
					? {
							...cat,
							evaluations: cat.evaluations?.map(evaluation => (evaluation?.index === questionIndex ? { ...evaluation, remark: '' } : evaluation)),
					  }
					: cat,
			);
		});
	};
	async function submitReport() {
		const hasErrors = errorCheck();
		if (hasErrors) {
			return;
		}
		try {
			await createEndEvaluationReport({
				variables: {
					caseId,
					formData: { evaluationCategories: evaluationCategories, deviations, signature: signature?.signatureDetails?.fileName === signatureFile?.name ? signature : undefined },
					onlySave: false,
				},
			});

			history.goBack();
		} catch (e) {
			console.error(e);
		}
	}

	React.useEffect(() => {
		setSaved(false);
	}, []);

	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createEndEvaluationReport({
				variables: {
					caseId,
					formData: { evaluationCategories: evaluationCategories, deviations, signature },
					onlySave: true,
				},
			});
			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}
	const resetForm = () => {
		setDeviations([]);
		setSignatureFile(null);
		setEvaluationCategories(populateEmptyLocalstorage());
	};

	const errorCheck = (): boolean => {
		const evaluationErrors: EvaluationError[] = [];
		const remarkErrors: EvaluationError[] = [];
		const deviationErrors: DeviationError[] = [];

		evaluationCategories.forEach(
			(category, categoryIndex) =>
				category.evaluations?.forEach((evaluation, questionIndex) => {
					if (evaluation?.answer === EvaluationEnum.NotSelected) {
						evaluationErrors.push({ categoryIndex, questionIndex });
					}
					if (evaluation?.answer === EvaluationEnum.NotRelevant && evaluation?.remark === '') {
						remarkErrors.push({ categoryIndex, questionIndex });
					}
				}),
		);
		deviations.forEach((deviation, reportIndex) => {
			if (deviation.reportDate === '' || deviation.reportDate === 'NaN-aN-aN') {
				deviationErrors.push({ reportIndex, key: 'reportDate' });
			}
			if (deviation.severityLevel === DeviationColor.NonSelected) {
				deviationErrors.push({ reportIndex, key: 'severityLevel' });
			}
			if (deviation.description === '') {
				deviationErrors.push({ reportIndex, key: 'description' });
			}
			if (deviation.cause === '') {
				deviationErrors.push({ reportIndex, key: 'cause' });
			}
		});

		setEvaluationError(evaluationErrors);
		setRemarkErrors(remarkErrors);
		setDeviationError(deviationErrors);

		return evaluationErrors.length > 0 || deviationErrors.length > 0 || remarkErrors.length > 0;
	};
	const { isUploading, uploadSignature } = useUploadSignature();
	if (caseReportsAndDocumentsLoading) return <Loading />;

	return (
		<>
			<Header title="reports.reportTypes.endevaluation" backButton />

			<div className="space-y-8 px-10 pb-10 text-sm">
				<ReportStaticInfo caseId={caseId} />

				<Box full icon={faSync} iconText="reports.reset" title="" onClick={() => resetForm()}>
					{EvaluationQuestions.map((category, categoryIndex) => {
						return (
							<Fragment key={category.category}>
								<div className="text-2xl mt-4 font-bold text-blue">{category.category}</div>
								{category.questions.map((question, questionIndex) => {
									const selectedAnswer =
										evaluationCategories.find(cat => cat.index === categoryIndex)?.evaluations?.find(evaluation => evaluation?.index === question.index)?.answer ??
										EvaluationEnum.NotSelected;
									return (
										<Fragment key={question.question}>
											<div className="ml-5 mt-5">
												<Radio
													horizontal
													className="mt-4 flex flex-row"
													title={question.question}
													options={EvaluationOptions}
													name={`${category.category}-${question.index}`}
													onChange={e => {
														const selectedAnswer = e.target.value as EvaluationEnum;
														handleEvaluationChange(categoryIndex, question.index, 'answer', selectedAnswer);
													}}
													value={selectedAnswer}
													errorMessage={throwEvaluationError(categoryIndex, questionIndex, evaluationErrors)}
												/>
												{selectedAnswer === EvaluationEnum.NotRelevant && (
													<TextAreaInput
														label="reports.asbestos.remark"
														value={
															evaluationCategories.find(cat => cat.index === categoryIndex)?.evaluations?.find(evaluation => evaluation?.index === question.index)?.remark
														}
														onChange={e => {
															handleEvaluationChange(categoryIndex, question.index, 'remark', e);
														}}
														placeholder={'reports.asbestos.remarkPlaceholder'}
														errorMessage={throwRemarkError(categoryIndex, questionIndex, remarkErrors)}
													/>
												)}
											</div>
										</Fragment>
									);
								})}
							</Fragment>
						);
					})}
					{deviations.length > 0 && (
						<>
							<div className="text-2xl mt-4 font-bold text-blue">{t('reports.asbestos.deviationTitles')}</div>
							<div className="flex flex-wrap">
								<div className="w-full">
									<ul className="mb-0 flex list-none flex-row flex-wrap pb-4" role="tablist">
										{deviations.map((deviation, idx) => (
											<li key={deviation.relatedEvaluationQuestion} className="-mb-px mr-2 flex-auto text-center last:mr-0">
												{idx === 0}{' '}
												<a
													className={
														'block rounded mt-2 px-5 py-3 text-xs font-bold uppercase leading-normal shadow-lg ' +
														(activeTab === idx ? 'bg-blue-calm text-white' : 'text-blue-calm bg-white')
													}
													onClick={e => {
														e.preventDefault();
														setActiveTab(idx);
													}}
													data-toggle="tab"
													href={'#link' + idx}
													role="tablist"
												>
													{typeof deviationErrors.find(deviation => deviation.reportIndex === idx) !== 'undefined' && (
														<FontAwesomeIcon icon={faExclamationTriangle} className="text-red mr-1" size="lg" />
													)}
													{deviation.relatedEvaluationCategory}
												</a>
											</li>
										))}

										<li className="-mb-px mr-2 flex-auto text-center last:mr-0"></li>
									</ul>
									<div className="relative mb-6 flex w-full min-w-0 flex-col break-words rounded bg-white shadow-lg">
										<div className="flex-auto px-4 py-5">
											<div className="tab-content tab-space">
												{deviations.map((deviation, idx) => (
													<div key={deviation.relatedEvaluationQuestion} className={activeTab === idx ? 'block' : 'hidden'} id={'#link' + idx}>
														<FormFieldHeader largeTitle title={`${t('reports.asbestos.deviationTitle')} - ${deviation.relatedEvaluationQuestion}`} />
														<div className="mt-4">
															<Datepicker
																title={'reports.asbestos.deviationReportDate'}
																name={`deviationReportDate-${idx}`}
																value={deviation.reportDate}
																onChange={e => handleDeviationChange(idx, 'reportDate', e.target.value)}
																errorMessage={throwDeviationError(idx, 'reportDate', deviationErrors)}
															/>
															<Radio
																name={`severityLevel-${idx}`}
																options={DeviationColorOptions}
																title={'reports.asbestos.deviationSeverity'}
																onChange={e => {
																	const color = e.target.value as DeviationColor;
																	handleDeviationChange(idx, 'severityLevel', color);
																}}
																value={deviation.severityLevel}
																horizontal
																errorMessage={throwDeviationError(idx, 'severityLevel', deviationErrors)}
															/>
															<TextAreaInput
																label="reports.asbestos.description"
																value={deviation.description}
																onChange={e => handleDeviationChange(idx, 'description', e)}
																placeholder={'reports.asbestos.descriptionPlaceholder'}
																errorMessage={throwDeviationError(idx, 'description', deviationErrors)}
															/>
															<TextAreaInput
																label="reports.asbestos.cause"
																value={deviation.cause}
																onChange={e => handleDeviationChange(idx, 'cause', e)}
																placeholder={'reports.asbestos.causePlaceholder'}
																errorMessage={throwDeviationError(idx, 'cause', deviationErrors)}
															/>
															<TextAreaInput
																label="reports.asbestos.correctingAction"
																value={deviation.action}
																onChange={e => handleDeviationChange(idx, 'action', e)}
																placeholder={'reports.asbestos.actionPlaceholder'}
															/>
															<TextInput
																label={'reports.asbestos.actionResponsible'}
																value={deviation.actionResponsible}
																onChange={e => handleDeviationChange(idx, 'actionResponsible', e)}
															/>
															<Datepicker
																title={'reports.asbestos.actionDate'}
																name={`actionDate-${idx}`}
																value={deviation.actionDate ?? ''}
																onChange={e =>
																	e.target.value === '' || e.target.value === 'NaN-aN-aN'
																		? handleDeviationChange(idx, 'actionDate', null)
																		: handleDeviationChange(idx, 'actionDate', e.target.value)
																}
															/>
														</div>
													</div>
												))}
											</div>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
					<div className="flex gap-1">
						<div className="mt-4 text-base font-semibold text-blue">{t('reports.asbestos.signature')}</div>
						<div className="mt-4 text-base font-semibold text-blue lowercase">{t('case.areaResponsible')}</div>
					</div>
					<div className="flex mt-2 gap-3">
						<div className="  font-medium text-blue">{t('reports.asbestos.signed') + ': '} </div>

						{signatureFile !== undefined && signatureFile !== null && signatureFile?.name === signature?.signatureDetails?.fileName ? (
							<div className="align-center">
								<FontAwesomeIcon icon={faCheck} className="text-green" size="lg" />
							</div>
						) : (
							<div className="align-center">
								<FontAwesomeIcon icon={faTimes} className="text-red" size="lg" />
							</div>
						)}
						<FontAwesomeIcon
							icon={faEdit}
							size="lg"
							className="cursor-pointer"
							onClick={() => {
								setShowSignatureModal(true);
							}}
						/>
					</div>
				</Box>

				<div className="ml-4 my-2 mr-6 flex flex-row space-x-2">
					<Button icon={faFilePdf} onClick={() => submitReport()} disabled={submitting} loading={submitting && loadingType === 'CREATE'} success text="reports.createPdfReport" />

					{saveButtonReportsFlag && (
						<Button
							disabled={submitting}
							loading={submitting && loadingType === 'SAVE'}
							primary
							onClick={() => saveReport()}
							text={saved ? '' : 'reports.saveReport'}
							icon={saved ? faCheck : undefined}
						/>
					)}
				</div>
			</div>
			<Modal
				visible={showSignatureModal}
				close={() => {
					setShowSignatureModal(false);
				}}
				title={''}
				body={
					<div className="p-1">
						<SignatureInput
							required={false}
							label={t('reports.asbestos.signature')}
							isUploading={isUploading}
							signatureFile={signatureFile ?? undefined}
							onClear={() => {
								setSignatureFile(null);
								setSignature({});
							}}
							onSubmit={async signature => {
								try {
									const fileName = `signature-${Date.now()}.${signature.extension}`;
									const uploadResponse = await uploadSignature(signature.base64, fileName, erpNumber);

									if (uploadResponse?.ok) {
										setSignatureFile({
											thumbnail: signature.base64,
											extension: signature.extension,
											url: '',
											created: '',
											name: fileName,
											fileId: '',
											groupingKey: '',
											metadata: [],
											comments: '',
										});
										setSignature({ signatureDetails: { fileName } });
										setShowSignatureModal(false);
									} else {
										setSignatureFile(null);
										setSignature({});
									}
								} catch (error) {
									console.error(error);
									setFileError(true);
								}
							}}
							errorMessage={fileError ? t('common.couldNotUpload') : undefined}
						/>
					</div>
				}
			/>
		</>
	);
};

export default EndEvaluationReport;
